import { useState } from "react";
import { Button } from "@/components/ui/button";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { Trash2, Pencil } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePropertyUnits } from "./property-units.hooks";
import { PropertyUnit } from "@/models/properties.model";
import { UnitModal } from "@/app/pmc/properties-detail/properties-detail.components/property-units/unit-modal.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function PropertyUnitsTab({ propertyId }: { propertyId: string }) {
  const {
    units,
    total,
    page,
    pageSize,
    unitNumber,
    setPage,
    setPageSize,
    setSearch,
    handleDelete,
    creating,
    openCreateModal,
    closeCreateModal,
    editing,
    openEditModal,
    closeEditModal,
    handleSave,
    handleUpdate,
  } = usePropertyUnits(propertyId);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const columns: ColumnDef<PropertyUnit>[] = [
    {
      header: "Unit",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <Avatar className="rounded-none">
            <AvatarImage className="rounded-none" src={row.floor_plan} />
            <AvatarFallback className="rounded-none">{row.unit_number.charAt(0)}</AvatarFallback>
          </Avatar>
          <span>{row.unit_number}</span>
        </div>
      ),
    },
    // { header: "Floor Plan", accessor: "floor_plan" },
    { header: "Beds", accessor: "beds" },
    { header: "Baths", accessor: "baths" },
    {
      header: "Actions",
      accessor: (row) => (
        <div className="flex justify-end space-x-2">
          <Button size="sm" variant="secondary" onClick={() => openEditModal(row)}>
            <Pencil className="w-3 h-3" />
          </Button>

          <Dialog open={deletingId === row.id} onOpenChange={(o) => setDeletingId(o ? row.id : null)}>
            <DialogTrigger asChild>
              <Button size="sm" variant="destructive">
                <Trash2 className="w-3 h-3" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Unit</DialogTitle>
              </DialogHeader>
              <p className="mt-2 text-sm text-muted-foreground">
                Are you sure you want to delete unit <strong>{row.unit_number}</strong>? This cannot be undone.
              </p>
              <DialogFooter className="mt-4 flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setDeletingId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    try {
                      await handleDelete(row.id);
                      toast.success("Unit deleted");
                    } catch {
                      toast.error("Failed to delete");
                    } finally {
                      setDeletingId(null);
                    }
                  }}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ),
      className: "justify-end",
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={openCreateModal}>+ Add Unit</Button>
      </div>

      <SimpleTable<PropertyUnit>
        columns={columns}
        data={units}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        filters={[
          {
            type: "search",
            title: "Search Unit Number",
            value: unitNumber,
            onValueChange: setSearch,
          },
        ]}
        onReset={() => setSearch("")}
      />

      {/* Create */}
      <UnitModal
        open={creating}
        onOpenChange={(o) => !o && closeCreateModal()}
        onSubmit={(vals) => handleSave({ 
          unit_number: vals.unit_number, 
          floor_plan: vals.floor_plan || "", 
          property: propertyId,
          beds: vals.beds,
          baths: vals.baths,
        })}
      />

      {/* Edit */}
      <UnitModal
        open={!!editing}
        unit={editing!}
        onOpenChange={(o) => !o && closeEditModal()}
        onSubmit={(vals) =>
          editing && handleUpdate(editing.id, { unit_number: vals.unit_number, floor_plan: vals.floor_plan || "", beds: vals.beds, baths: vals.baths })
        }
      />
    </>
  );
}
